// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    canCheckoutWithCart,
    canSubmitRequisition,
    getCurrentCartState,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@werner/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@werner/App/Components/MiniCart/MiniCart.helpers";
import MiniCartLineCard from "@werner/App/Components/MiniCart/MiniCartLineCard";
import Notify from "@werner/App/Components/Notify";
import { xisCheckoutDisabled } from "@werner/App/Store/Selectors/GeneralSelectors";
import CaretRight from "@werner/XDL/Icons/CaretRight";
import { color, modalButtonsActionStyles, styledScrollbar } from "@werner/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

interface OwnProps {
    isPopupShown?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const isCartLoading = cartState.isLoading;
    const { isUpdatingCart } = state.context;

    return {
        isCheckoutDisabled: xisCheckoutDisabled(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        totalCountDisplay: cart?.totalCountDisplay || 0,
        orderSubTotalDisplay: cart?.orderSubTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        checkoutPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        cart,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isUpdatingCart,
        isLoading: isCartLoading,
        isCartEmpty: isCartEmpty(cart),
    };
};

const mapDispatchToProps = {
    doPunchOutCheckout,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    submitRequisition,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface MiniCartStyles {
    title?: LinkPresentationProps;
    cartLinesWrapper?: InjectableCss;
    cartLinesHeader?: InjectableCss;
    cartLineHeaderColumn?: InjectableCss;
    cartLineHeaderColumnText?: TypographyProps;
    summarySectionWrapper?: InjectableCss;
    itemsCountWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    itemsCount?: TypographyProps;
    label?: TypographyProps;
    subTotalWrapper?: InjectableCss;
    subTotalValue?: TypographyProps;
    actionWrapper?: InjectableCss;
    viewCartButton?: ButtonPresentationProps;
    checkoutButton?: ButtonPresentationProps;
    noCartLinesNotify?: TypographyProps;
    notifyWrapper?: InjectableCss;
    centerWrapper?: InjectableCss;
    cantCheckoutCart?: InjectableCss;
    emptyCartText?: TypographyProps;
    rightSide?: InjectableCss;
    leftSide?: InjectableCss;
}

const miniCartStyles: MiniCartStyles = {
    title: {
        color: "text.main",
        typographyProps: {
            size: 28,
            lineHeight: "32px",
            weight: 500,
            css: css`
                letter-spacing: 0;
                padding: 35px 30px;
                text-align: center;
                width: 750px;
                display: flex;
                justify-content: center;

                &&&:hover {
                    color: ${color("text.link")};
                }
            `,
        },
        css: css`
            display: flex;
            width: 100%;
            ${({ hasCartLines }: any) =>
                !hasCartLines &&
                css`
                    &&&& ${TypographyStyle} {
                        border-bottom: 1px solid ${color("common.border")};
                        margin: 0 30px;
                    }
                `}
        `,
    },
    cartLinesWrapper: {
        css: css`
            position: relative;
            min-height: 90px;
            max-height: calc(100vh - 450px);
            overflow-x: hidden;
            width: 100%;
            ${styledScrollbar(6)}
        `,
    },
    cartLinesHeader: {
        css: css`
            display: flex;
            background: ${color("primary", 0.05)};
            padding: 12px 30px;
        `,
    },
    cartLineHeaderColumn: {
        css: css`
            display: flex;
            width: 100%;
        `,
    },
    cartLineHeaderColumnText: {
        size: 15,
        lineHeight: "22px",
        weight: 500,
    },
    summarySectionWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    infoWrapper: {
        css: css`
            display: flex;
            gap: 60px;
            flex-wrap: wrap;
            padding: 15px 0;
            padding-right: 60px;
            background: ${color("primary", 0.05)};
            margin-bottom: 25px;
            box-shadow: 0 0 15px 0 ${color("primary", 0.05)};
        `,
    },
    itemsCountWrapper: {
        css: css`
            margin-left: auto;
            display: flex;
            gap: 8px;
        `,
    },
    itemsCount: {
        size: 18,
        weight: 500,
        lineHeight: "18px",
    },
    label: {
        size: 18,
        lineHeight: "18px",
        weight: 500,
        css: css`
            &:after {
                content: ":";
            }
        `,
    },
    subTotalWrapper: {
        css: css`
            display: flex;
            gap: 8px;
        `,
    },
    subTotalValue: {
        size: 18,
        weight: 500,
        lineHeight: "18px",
    },
    actionWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            position: relative;
            justify-content: space-between;
            padding: 0 30px 30px 30px;
        `,
    },
    checkoutButton: {
        sizeVariant: "large",
        mergeCss: true,
        typographyProps: {
            size: 16,
            lineHeight: "18px",
            weight: 400,
            css: css`
                width: 340px;
                text-align: left;
            `,
        },
    },
    viewCartButton: {
        ...modalButtonsActionStyles.cancelButton,
    },
    notifyWrapper: {
        css: css`
            border-top: 0;
            padding-bottom: 45px;
        `,
    },
    centerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 100%;
            min-width: 250px;
            min-height: 200px;
            justify-content: center;
        `,
    },
    cantCheckoutCart: {
        css: css`
            margin: 16px 30px 0;
        `,
    },
    emptyCartText: {
        variant: "h5",
        as: "p",
        css: css`
            padding: 20px 0;
            text-align: center;
        `,
    },

    leftSide: {
        css: css`
            display: flex;
            max-width: 400px;
            width: 100%;
        `,
    },
    rightSide: {
        css: css`
            display: flex;
            width: 100%;
            max-width: 269px;
            justify-content: space-between;
        `,
    },
};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    canCheckoutWithCart,
    canSubmitRequisition,
    cartPageUrl,
    checkoutPageUrl,
    doPunchOutCheckout,
    isCheckoutDisabled,
    isLoading,
    isPunchOutOrder,
    isUpdatingCart,
    isUpdatingCartLine,
    orderSubTotalDisplay,
    preloadCheckoutShippingData,
    requisitionConfirmationPageUrl,
    totalCountDisplay,
    isPopupShown,
    removeCartLine,
    updateCartLine,
    isCartEmpty,
    submitRequisition,
}) => {
    const history = useHistory();
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }
        setIsCheckoutLoading(true);
        preloadCheckoutShippingData({
            onSuccess: () => {
                setIsCheckoutLoading(false);
                history.push(checkoutPageUrl!);
            },
        });
    };

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCartClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        hideTippy();
        history.push(cartPageUrl);
    };

    if (!cart && isLoading) {
        return (
            <>
                <Link {...styles.title} href={cartPageUrl} {...{ hasCartLines: false }} aria-label="Cart Summary link">
                    {translate("Cart Summary")}
                </Link>
                <StyledWrapper {...styles.centerWrapper}>
                    <LoadingSpinner />
                </StyledWrapper>
            </>
        );
    }

    if (!cart) {
        return null;
    }

    const cartLines = cart.cartLines ?? [];

    if (!cartLines?.length || isCartEmpty) {
        return (
            <>
                <Link {...styles.title} href={cartPageUrl} {...{ hasCartLines: false }} aria-label="Cart Summary Link">
                    {translate("Cart Summary")}
                </Link>
                <Typography {...styles.emptyCartText}>{siteMessage("Cart_NoOrderLines")}</Typography>
            </>
        );
    }

    const columns = (
        <>
            <StyledWrapper {...styles.leftSide}>
                <StyledWrapper {...styles.cartLineHeaderColumn}>
                    <Typography {...styles.cartLineHeaderColumnText}>{translate("Item Description")}</Typography>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.rightSide}>
                <StyledWrapper {...styles.cartLineHeaderColumn}>
                    <Typography {...styles.cartLineHeaderColumnText}>{translate("QTY")}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.cartLineHeaderColumn}>
                    <Typography {...styles.cartLineHeaderColumnText}>{translate("Price/Unit")}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.cartLineHeaderColumn}>
                    <Typography {...styles.cartLineHeaderColumnText}>{translate("Total")}</Typography>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );

    const productsCannotBePurchased = cartLines.some(o => o.isRestricted || !o.isActive);
    const checkoutLabel = isPunchOutOrder
        ? translate("Return to Procurement System")
        : cart?.requiresApproval
        ? translate("Checkout for Approval")
        : translate("Checkout Now");
    return (
        <LoadingOverlay loading={isLoading || isUpdatingCart || isUpdatingCartLine}>
            <Link {...styles.title} href={cartPageUrl} {...{ hasCartLines: true }} aria-label="Cart Summary link">
                {translate("Cart Summary")}
            </Link>
            {cartLines.length > 0 && <StyledWrapper {...styles.cartLinesHeader}>{columns}</StyledWrapper>}
            <StyledWrapper {...styles.cartLinesWrapper} ref={parentBoxRef} data-test-selector="cartLines-container">
                {cartLines.map(cartLine => {
                    const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                    return (
                        <MiniCartLineCard
                            key={`miniCartLine_${cartLine.id}`}
                            cartLine={cartLine}
                            cart={cart}
                            hideTippy={hideTippy}
                            qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                            removeCartLineClickHandler={removeCartLineClickHandler}
                            errorMessages={errorMessages}
                        />
                    );
                })}
            </StyledWrapper>
            {cartLines.length > 0 && (
                <StyledWrapper {...styles.summarySectionWrapper}>
                    <StyledWrapper {...styles.infoWrapper}>
                        <StyledWrapper {...styles.itemsCountWrapper}>
                            <Typography {...styles.label}>{translate("Items")}</Typography>
                            <Typography {...styles.itemsCount}>{totalCountDisplay}</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.subTotalWrapper}>
                            <Typography {...styles.label}>{`${translate("Subtotal")}`}</Typography>
                            <Typography {...styles.subTotalValue}>{orderSubTotalDisplay}</Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.actionWrapper}>
                        {canCheckoutWithCart && (
                            <>
                                {!canSubmitRequisition ? (
                                    <Button
                                        {...styles.checkoutButton}
                                        onClick={checkoutHandler}
                                        disabled={
                                            isCheckoutDisabled ||
                                            productsCannotBePurchased ||
                                            isCheckoutLoading ||
                                            isUpdatingCart ||
                                            isLoading
                                        }
                                        aria-label={`${checkoutLabel}`}
                                    >
                                        {checkoutLabel}
                                    </Button>
                                ) : (
                                    <Button
                                        {...styles.checkoutButton}
                                        onClick={submitRequisitionClickHandler}
                                        data-test-selector="cartTotal_SubmitRequisition"
                                        aria-label="Submit Requisition"
                                    >
                                        {translate("Submit Requisition")}
                                    </Button>
                                )}
                            </>
                        )}
                        <Button
                            {...styles.viewCartButton}
                            onClick={handleViewCartClick}
                            aria-label="View Shopping Cart"
                        >
                            {translate("View Shopping Cart")}
                        </Button>
                    </StyledWrapper>
                    {productsCannotBePurchased && (
                        <StyledWrapper {...styles.cantCheckoutCart}>
                            <Notify type="danger" data-test-selector="cartlineHeader_warningProductsCannotBePurchased">
                                {siteMessage("Cart_ProductsCannotBePurchased")}
                            </Notify>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MiniCart);
